import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { api } from "../api";
import { Input } from "./ui";

export interface Chat {
  chat_id: string;
  title: string | null;
  chat_type: string;
  assigned_tenant_id: number | null;
  last_seen_at: string;
}

export function useChats() {
  return useQuery<Chat[]>({
    queryKey: ["chats"],
    queryFn: () => api.get<Chat[]>("/api/admin/chats"),
  });
}

/**
 * Chat id entry backed by what the platform bot has actually seen.
 *
 * A datalist rather than a dropdown on purpose: the developer can pick a
 * known group by name, but can still type an id for a chat the bot has not
 * met yet — a group nobody has posted in since the bot joined still needs to
 * be configurable.
 */
export function ChatPicker({
  value,
  onChange,
  tenantId,
  placeholder = "-1001234567890",
}: {
  value: string;
  onChange: (v: string) => void;
  tenantId?: number;
  placeholder?: string;
}) {
  const listId = useId();
  const chats = useChats();

  // A chat already held by another customer would be rejected by the API, so
  // don't offer it here either.
  const options = (chats.data ?? []).filter(
    (c) => c.assigned_tenant_id === null || c.assigned_tenant_id === tenantId,
  );

  return (
    <>
      <Input
        list={listId}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {options.map((c) => (
          <option key={c.chat_id} value={c.chat_id}>
            {c.title ?? "untitled"} ({c.chat_type})
          </option>
        ))}
      </datalist>
    </>
  );
}
